import type { LevelConfig, RoundStats } from './types.js';

export const SCORING = {
  /** Points for any correct pair. */
  base: 10,
  /**
   * Points for one correct tile in a hunt round.
   *
   * Half a pair, because it is half the work: finding 8 among the evens is one
   * judgement, where 3 ↔ 7 is two numbers held at once. A hunt board also has
   * roughly twice as many scoring taps, so a round comes out worth about the
   * same — which matters when a class plays both in one session.
   */
  pickBase: 5,
  /** Added per step of the current combo, so a streak is worth protecting. */
  comboStep: 5,
  /** Cap so one long streak cannot decide a whole class round. */
  comboCap: 10,
  /** Up to this much extra for clearing with most of the clock left. */
  speedBonusMax: 8,
  /** Clearing the whole board. */
  clearBonus: 100,
  /** Each unused "Add row" at clear time. */
  unusedAddBonus: 15,
} as const;

/**
 * Points for one correct pair.
 *
 * `combo` is the streak length *including* this match, so the first match of a
 * streak scores `base`. Speed only pays while a clock is running.
 */
export function matchPoints(combo: number, cfg: LevelConfig, secondsLeft: number | null): number {
  const steps = Math.min(Math.max(combo - 1, 0), SCORING.comboCap);
  let pts = SCORING.base + steps * SCORING.comboStep;
  if (cfg.timerSec && secondsLeft !== null) {
    pts += Math.round(SCORING.speedBonusMax * (secondsLeft / cfg.timerSec));
  }
  return pts;
}

/**
 * Points for one correct tile in a hunt round. Same combo and speed curve as a
 * pair, so a streak is worth protecting in either mechanic.
 */
export function pickPoints(combo: number, cfg: LevelConfig, secondsLeft: number | null): number {
  const steps = Math.min(Math.max(combo - 1, 0), SCORING.comboCap);
  let pts = SCORING.pickBase + Math.round(steps * SCORING.comboStep * 0.5);
  if (cfg.timerSec && secondsLeft !== null) {
    pts += Math.round(SCORING.speedBonusMax * 0.5 * (secondsLeft / cfg.timerSec));
  }
  return pts;
}

export function missPoints(cfg: LevelConfig): number {
  return -Math.abs(cfg.missPenalty);
}

/** End-of-round bonus. Zero unless the board was actually emptied. */
export function clearBonus(stats: RoundStats, cfg: LevelConfig): number {
  if (!stats.cleared) return 0;
  const unused = Math.max(0, cfg.adds - stats.addsUsed);
  return SCORING.clearBonus + unused * SCORING.unusedAddBonus;
}

export const emptyRound = (): RoundStats => ({
  score: 0,
  matches: 0,
  misses: 0,
  maxCombo: 0,
  addsUsed: 0,
  hintsUsed: 0,
  cleared: false,
  elapsedMs: 0,
});
